<template>
  <app-layout>
    <div class="sc-container">
      <div class="page-wrap">

        <!-- ===== SIDEBAR ===== -->
        <aside class="sidebar-card">
          <!-- Avatar -->
          <div class="sb-avatar-wrap">
            <img
              v-if="url"
              :src="url"
              class="sb-avatar"
              alt=""
            />
            <img
              v-else-if="user.avatar"
              :src="$avatarSrc(user.avatar)"
              class="sb-avatar"
              alt=""
            />
            <img
              v-else
              src="/assets/avatar.png"
              class="sb-avatar"
              alt=""
            />
            <button type="button" class="sb-photo-btn" @click="showAvatarModal = true">Change Photo</button>
          </div>

          <!-- Name + profession -->
          <div class="sb-identity" v-if="user.first_name || user.profession">
            <p class="sb-name" v-if="user.first_name">{{ user.first_name }} {{ user.last_name }}</p>
            <p class="sb-prof" v-if="user.profession">{{ user.profession }}</p>
          </div>

          <!-- Stats -->
          <div class="sb-stats">
            <div class="sb-stat">
              <span class="sb-stat-val">{{ total_project }}</span>
              <span class="sb-stat-lbl">Projects</span>
            </div>
            <div class="sb-stat">
              <span class="sb-stat-val">{{ total_following }}</span>
              <span class="sb-stat-lbl">Following</span>
            </div>
          </div>

          <!-- Nav links -->
          <nav class="sb-nav">
            <router-link :to="'/sc/profile/' + id" class="sb-nav-link">View Profile</router-link>
            <a href="#basic-info" class="sb-nav-link sb-nav-link--active">Basic Info</a>
            <a href="#location" class="sb-nav-link">Location</a>
            <a href="#professional" class="sb-nav-link">Professional</a>
            <a href="#about" class="sb-nav-link">About</a>
          </nav>
        </aside>

        <!-- ===== MAIN FORM ===== -->
        <div class="form-sections">
          <form id="update-form" novalidate>

            <!-- Alert -->
            <div v-if="successMsg" class="sc-alert sc-alert--success">{{ successMsg }}</div>
            <div v-if="errorMsg" class="sc-alert sc-alert--error">{{ errorMsg }}</div>

            <!-- BASIC INFO -->
            <section class="main-card" id="basic-info">
              <div class="section-header">
                <h2 class="section-title">Basic Info</h2>
                <p class="section-sub">Your public identity on Structurecity.</p>
              </div>
              <div class="form-grid-2">
                <div class="sc-field">
                  <label>First Name <span class="req">*</span></label>
                  <input type="text" name="first_name" v-model="user.first_name" placeholder="First name" />
                </div>
                <div class="sc-field">
                  <label>Last Name <span class="req">*</span></label>
                  <input type="text" name="last_name" v-model="user.last_name" placeholder="Last name" />
                </div>
                <div class="sc-field">
                  <label>Email</label>
                  <input type="email" name="email" :value="user.email" readonly />
                </div>
                <div class="sc-field">
                  <label>Phone Number</label>
                  <input type="tel" name="phone_number" v-model="user.phone_number" />
                </div>
              </div>

              <!-- Birthday row -->
              <div class="form-grid-3">
                <div class="sc-field">
                  <label>Birth Month</label>
                  <select name="birth_month" v-model="user.birth_month">
                    <option value="" disabled>Month</option>
                    <option v-for="m in months" :key="m" :value="m">{{ m }}</option>
                  </select>
                </div>
                <div class="sc-field">
                  <label>Birth Day</label>
                  <select name="birth_day" v-model="user.birth_day">
                    <option value="" disabled>Day</option>
                    <option v-for="d in 31" :key="d" :value="String(d)">{{ d }}</option>
                  </select>
                </div>
                <div class="sc-field">
                  <label>Birth Year</label>
                  <select name="birth_year" v-model="user.birth_year">
                    <option value="" disabled>Year</option>
                    <option v-for="y in years" :key="y" :value="String(y)">{{ y }}</option>
                  </select>
                </div>
              </div>

              <div class="form-grid-2">
                <div class="sc-field">
                  <label>Gender</label>
                  <select name="gender" v-model="user.gender">
                    <option value="" disabled>Select</option>
                    <option value="Female">Female</option>
                    <option value="Male">Male</option>
                  </select>
                </div>
              </div>
            </section>

            <!-- LOCATION -->
            <section class="main-card" id="location">
              <div class="section-header">
                <h2 class="section-title">Location</h2>
                <p class="section-sub">Where are you based?</p>
              </div>
              <div class="form-grid-3">
                <div class="sc-field">
                  <label>Country</label>
                  <select name="country_id" v-model="user.country_id" @change="fetchStates2($event)">
                    <option value="" disabled>Country</option>
                    <option v-for="c in country" :key="c.id" :value="c.id">{{ c.name }}</option>
                  </select>
                </div>
                <div class="sc-field">
                  <label>State</label>
                  <select name="state_id" v-model="user.state_id" @change="fetchCities2($event)">
                    <option value="" disabled>State</option>
                    <option v-for="s in state" :key="s.id" :value="s.id">{{ s.name }}</option>
                  </select>
                </div>
                <div class="sc-field">
                  <label>City</label>
                  <select name="city_id" v-model="user.city_id">
                    <option value="" disabled>City</option>
                    <option v-for="c in city" :key="c.id" :value="c.id">{{ c.name }}</option>
                  </select>
                </div>
              </div>
              <div class="sc-field" style="margin-top:.75rem">
                <label>Address</label>
                <textarea name="address" v-model="user.address" rows="3" placeholder="Street address, area…"></textarea>
              </div>
            </section>

            <!-- PROFESSIONAL INFO -->
            <section class="main-card" id="professional">
              <div class="section-header">
                <h2 class="section-title">Professional Info</h2>
                <p class="section-sub">Your career and company details.</p>
              </div>
              <div class="form-grid-2">
                <div class="sc-field">
                  <label>Profession / Career Title</label>
                  <select name="profession" v-model="user.profession">
                    <option value="">-- Select --</option>
                    <option v-for="p in professions" :key="p.id" :value="p.name">{{ p.name }}</option>
                  </select>
                </div>
                <div class="sc-field">
                  <label>Personal Website</label>
                  <input type="url" name="website" v-model="user.website" placeholder="https://" />
                </div>
                <div class="sc-field">
                  <label>Current Position</label>
                  <input type="text" name="current_position" v-model="user.current_position" placeholder="e.g. Senior Architect" />
                </div>
                <div class="sc-field">
                  <label>Company</label>
                  <multiselect
                    v-model="user.current_position_company_id"
                    label="name"
                    placeholder="Select company"
                    :options="companies"
                    :allow-empty="true"
                  ></multiselect>
                </div>
              </div>

              <!-- Membership & Certification -->
              <div v-if="membership || certification" class="credentials-block">
                <h3 class="cred-title">Membership &amp; Certification</h3>
                <div class="form-grid-2" v-if="membership">
                  <div class="sc-field">
                    <label>
                      Membership Number
                      <span class="cred-name">{{ membership.name }} ({{ membership.code }})</span>
                      <span v-if="user.membership">
                        <span v-if="user.membership.status == 0" class="badge badge--warn">Awaiting</span>
                        <span v-if="user.membership.status == 1" class="badge badge--ok">Approved</span>
                        <span v-if="user.membership.status == 2" class="badge badge--err">Rejected</span>
                      </span>
                    </label>
                    <input type="text" name="membership_number" :value="user.membership ? user.membership.membership_number : ''" :readonly="approved_membership" />
                    <input type="hidden" name="membership_id" :value="membership ? membership.id : ''" />
                  </div>
                </div>
                <div class="form-grid-2" v-if="certification">
                  <div class="sc-field">
                    <label>
                      Certificate Number
                      <span class="cred-name">{{ certification.name }} ({{ certification.code }})</span>
                      <span v-if="user.certification">
                        <span v-if="user.certification.status == 0" class="badge badge--warn">Awaiting</span>
                        <span v-if="user.certification.status == 1" class="badge badge--ok">Approved</span>
                        <span v-if="user.certification.status == 2" class="badge badge--err">Rejected</span>
                      </span>
                    </label>
                    <input type="text" name="certificate_number" :value="user.certification ? user.certification.certificate_number : ''" :readonly="approved_certification" />
                    <input type="hidden" name="certification_id" :value="certification ? certification.id : ''" />
                  </div>
                </div>
              </div>
            </section>

            <!-- ABOUT -->
            <section class="main-card" id="about">
              <div class="section-header">
                <h2 class="section-title">About</h2>
                <p class="section-sub">Tell people about yourself in a few sentences.</p>
              </div>
              <div class="sc-field">
                <textarea name="about" v-model="user.about" rows="5" placeholder="Write your bio..."></textarea>
              </div>
            </section>

            <!-- INTERESTS -->
            <section class="main-card">
              <div class="section-header">
                <h2 class="section-title">Interests</h2>
                <p class="section-sub">Select topics you care about.</p>
              </div>
              <div class="interests-grid">
                <label
                  v-for="item in interests"
                  :key="item.id"
                  class="interest-chip"
                >
                  <input type="checkbox" name="interests[]" :value="item.id" />
                  <span>{{ item.name }}</span>
                </label>
              </div>
            </section>

            <!-- Save bar -->
            <div class="save-bar">
              <router-link to="/" class="btn-ghost">Cancel</router-link>
              <button type="button" class="btn-primary" :disabled="loading" @click="update()">
                <span v-if="loading" class="sc-spinner"></span>
                <span v-else>Save Changes</span>
              </button>
            </div>

          </form>
        </div>
      </div>
    </div>

    <!-- ===== AVATAR CROP MODAL ===== -->
    <div v-if="showAvatarModal" class="modal-overlay" @click.self="showAvatarModal = false">
      <div class="modal-panel modal-panel--wide">
        <div class="modal-header">
          <h2 class="modal-title">Change Profile Picture</h2>
          <button type="button" class="modal-close" @click="showAvatarModal = false">
            <svg width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
        </div>
        <div class="modal-body">
          <div class="sc-field">
            <label>Select Image <span class="req">*</span></label>
            <input type="file" accept="image/*" @change="setImage" />
          </div>
          <div v-if="imageSrc" class="cropper-wrap">
            <vue-cropper
              ref="cropper"
              :guides="true"
              :aspect-ratio="500 / 500"
              :src="imageSrc"
              class="cropper-box"
            ></vue-cropper>
            <img v-if="croppedImageSrc" :src="croppedImageSrc" class="cropper-preview" alt="preview" />
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn-ghost" @click="showAvatarModal = false">Cancel</button>
          <button v-if="imageSrc && !croppedImageSrc" type="button" class="btn-outline" @click="cropImage">Crop</button>
          <button v-if="croppedImageSrc" type="button" class="btn-primary" :disabled="loading" @click="uploadImage()">
            <span v-if="loading" class="sc-spinner"></span>
            <span v-else>Upload</span>
          </button>
        </div>
      </div>
    </div>
  </app-layout>
</template>

<script>
import AppLayout from '@/layouts/AppLayout.vue';
import axios from 'axios';
import VueCropper from 'vue-cropperjs';
import 'cropperjs/dist/cropper.css';
import '@vueup/vue-quill/dist/vue-quill.snow.css';
import Multiselect from 'vue-multiselect';

export default {
  name: 'UpdateProfile',
  components: {
    AppLayout,
    VueCropper,
    Multiselect,
  },

  created() {
    this.fetchCompany();
    this.fetchUser();
    this.fetchCountries();
    this.fectchInterests();
    this.fetchProfessions();
    this.fetchCompanies();
  },

  watch: {
    $route: {
      immediate: true,
      handler(to) {
        document.title = to.meta.title || 'Update Profile';
      },
    },
  },

  data() {
    return {
      company: {},
      user: {},
      country: [],
      state: [],
      city: [],
      companies: [],
      total_project: 0,
      total_followers: 0,
      total_following: 0,
      loading: false,
      id: localStorage.getItem('slug'),
      interests: [],
      professions: [],
      url: null,
      certification: null,
      membership: null,
      approved_membership: false,
      approved_certification: false,
      imageSrc: '',
      croppedImageSrc: '',
      showAvatarModal: false,
      successMsg: '',
      errorMsg: '',
      months: ['January','February','March','April','May','June','July','August','September','October','November','December'],
    };
  },

  computed: {
    image_url() {
      return process.env.VUE_APP_IMAGE_URL;
    },
    years() {
      const result = [];
      for (let y = 2017; y >= 1900; y--) result.push(y);
      return result;
    },
  },

  methods: {
    logout() {
      localStorage.clear();
      this.$router.go();
    },

    fetchCompany() {
      axios(process.env.VUE_APP_API_URL + 'company').then(response => {
        if (response.status !== 200) return;
        this.company = response.data;
      }).catch(() => {});
    },

    fetchUser() {
      axios(process.env.VUE_APP_API_URL + 'user/details/' + localStorage.getItem('user')).then(response => {
        if (response.status !== 200) return;
        this.user = response.data;
        this.user.current_position_company_id = response.data.currentcompany;
        this.fetchTotalProjects(this.user.id);
        this.fetchTotalFollowers(this.user.id);
        this.fetchTotalFollowing(this.user.id);
        this.fetchStates();
        this.fetchCities();
        this.fetchCertification(this.user.profession);
        this.fetchMembership(this.user.profession);
        if (this.user.membership && this.user.membership.status == 1) this.approved_membership = true;
        if (this.user.certification && this.user.certification.status == 1) this.approved_certification = true;
      }).catch(() => {});
    },

    fetchCountries() {
      axios(process.env.VUE_APP_API_URL + 'country').then(response => {
        if (response.status !== 200) return;
        this.country = response.data;
      }).catch(() => {});
    },

    fetchStates() {
      axios(process.env.VUE_APP_API_URL + 'state/' + this.user.country_id).then(response => {
        if (response.status !== 200) return;
        this.state = response.data;
      }).catch(() => {});
    },

    fetchCities() {
      axios(process.env.VUE_APP_API_URL + 'city/' + this.user.state_id).then(response => {
        if (response.status !== 200) return;
        this.city = response.data;
      }).catch(() => {});
    },

    fetchStates2(event) {
      axios(process.env.VUE_APP_API_URL + 'state/' + event.target.value).then(response => {
        if (response.status !== 200) return;
        this.state = response.data;
      }).catch(() => {});
    },

    fetchCities2(event) {
      axios(process.env.VUE_APP_API_URL + 'city/' + event.target.value).then(response => {
        if (response.status !== 200) return;
        this.city = response.data;
      }).catch(() => {});
    },

    fectchInterests() {
      axios(process.env.VUE_APP_API_URL + 'interest/user/' + localStorage.getItem('user')).then(response => {
        if (response.status !== 200) return;
        this.interests = response.data;
      }).catch(() => {});
    },

    fetchProfessions() {
      axios(process.env.VUE_APP_API_URL + 'professions').then(response => {
        if (response.status !== 200) return;
        this.professions = response.data;
      }).catch(() => {});
    },

    fetchTotalProjects(id) {
      axios(process.env.VUE_APP_API_URL + 'project/total-project/' + id).then(response => {
        if (response.status !== 200) return;
        this.total_project = response.data;
      }).catch(() => {});
    },

    fetchTotalFollowers(id) {
      axios(process.env.VUE_APP_API_URL + 'user/total-following/' + id).then(response => {
        if (response.status !== 200) return;
        this.total_following = response.data;
      }).catch(() => {});
    },

    fetchTotalFollowing(id) {
      axios(process.env.VUE_APP_API_URL + 'user/total-followers/' + id).then(response => {
        if (response.status !== 200) return;
        this.total_followers = response.data;
      }).catch(() => {});
    },

    fetchCertification(id) {
      axios(process.env.VUE_APP_API_URL + 'certification/' + id).then(response => {
        if (response.status !== 200) return;
        this.certification = response.data;
      }).catch(() => {});
    },

    fetchMembership(id) {
      axios(process.env.VUE_APP_API_URL + 'membership/' + id).then(response => {
        if (response.status !== 200) return;
        this.membership = response.data;
      }).catch(() => {});
    },

    fetchCompanies() {
      axios(process.env.VUE_APP_API_URL + 'companies').then(response => {
        if (response.status !== 200) return;
        this.companies = response.data;
      }).catch(() => {});
    },

    update() {
      this.loading = true;
      this.successMsg = '';
      this.errorMsg = '';
      var form = document.getElementById('update-form');
      var _data = new FormData(form);
      _data.append('current_position_company_id', JSON.stringify(this.user.current_position_company_id));
      axios.post(process.env.VUE_APP_API_URL + 'user/update-profile/' + localStorage.getItem('user'), _data)
        .then(response => {
          if (response.status === 200) {
            this.successMsg = response.message || 'Profile updated successfully.';
            window.Fire && window.Fire.emit('refresh');
            this.loading = false;
          } else {
            this.errorMsg = (response.data && response.data.message) || 'Update failed.';
            this.loading = false;
          }
        })
        .catch(error => {
          this.errorMsg = (error.response && error.response.data && error.response.data.message) || 'An error occurred.';
          this.loading = false;
        });
    },

    removeAvatar() {
      axios(process.env.VUE_APP_API_URL + 'user/remove-avatar/' + localStorage.getItem('user')).then(response => {
        if (!response.status == 200) return;
        this.$router.go();
      }).catch(() => {});
    },

    onFileChange(e) {
      const file = e.target.files[0];
      this.url = URL.createObjectURL(file);
    },

    setImage(e) {
      const file = e.target.files[0];
      if (!file.type.includes('image/')) { alert('Please select an image file'); return; }
      if (typeof FileReader === 'function') {
        const reader = new FileReader();
        reader.onload = event => {
          this.imageSrc = event.target.result;
          this.$refs.cropper && this.$refs.cropper.replace(event.target.result);
        };
        reader.readAsDataURL(file);
      } else {
        alert('Sorry, FileReader API not supported');
      }
    },

    cropImage() {
      this.croppedImageSrc = this.$refs.cropper.getCroppedCanvas().toDataURL();
    },

    uploadImage() {
      var self = this;
      self.loading = true;
      this.$refs.cropper.getCroppedCanvas().toBlob(function(blob) {
        let formData = new FormData();
        formData.append('name', 'avatar-' + new Date().getTime());
        formData.append('file', blob);
        formData.append('id', localStorage.getItem('user'));
        axios.post(process.env.VUE_APP_API_URL + 'user/update-profile-image', formData)
          .then(response => {
            if (response.status === 200) {
              self.successMsg = response.message || 'Avatar updated.';
              self.loading = false;
              self.showAvatarModal = false;
              self.imageSrc = '';
              self.croppedImageSrc = '';
              window.Fire && window.Fire.emit('refresh');
            } else {
              self.errorMsg = response.message || 'Upload failed.';
              self.loading = false;
            }
          })
          .catch(error => {
            self.errorMsg = (error.response && error.response.message) || 'Upload error.';
            self.loading = false;
          });
      });
    },
  },

  mounted() {
    window.Fire && window.Fire.on('refresh', () => {
      this.fetchUser();
      this.fectchInterests();
    });
  },
};
</script>

<style scoped>
.page-wrap {
  display: grid;
  grid-template-columns: 260px 1fr;
  gap: 1.5rem;
  padding: 2.5rem 0;
  align-items: start;
}
.sidebar-card {
  background: #fff;
  border-radius: var(--radius-lg);
  border: 1px solid var(--border-light);
  box-shadow: var(--shadow-sm);
  padding: 1.5rem;
  position: sticky;
  top: 80px;
}
.main-card {
  background: #fff;
  border-radius: var(--radius-lg);
  border: 1px solid var(--border-light);
  box-shadow: var(--shadow-sm);
  padding: 1.5rem 2rem;
  margin-bottom: 1.25rem;
}

/* Sidebar */
.sb-avatar-wrap { display: flex; flex-direction: column; align-items: center; gap: .75rem; margin-bottom: 1.25rem; }
.sb-avatar { width: 88px; height: 88px; border-radius: 50%; object-fit: cover; border: 3px solid var(--border-light); }
.sb-photo-btn {
  font-size: .8125rem;
  font-weight: 600;
  color: var(--primary);
  background: none;
  border: 1.5px solid var(--primary);
  border-radius: var(--radius-full);
  padding: .3rem .875rem;
  cursor: pointer;
  transition: background .15s, color .15s;
}
.sb-photo-btn:hover { background: var(--primary); color: #fff; }
.sb-identity { text-align: center; margin-bottom: 1rem; }
.sb-name { font-weight: 700; font-size: 1rem; color: var(--text); margin: 0 0 .25rem; }
.sb-prof { font-size: .8125rem; color: var(--text-muted); margin: 0; }
.sb-stats { display: flex; gap: .5rem; margin-bottom: 1.25rem; border-top: 1px solid var(--border-light); border-bottom: 1px solid var(--border-light); padding: .75rem 0; }
.sb-stat { flex: 1; display: flex; flex-direction: column; align-items: center; }
.sb-stat-val { font-size: 1.125rem; font-weight: 700; color: var(--text); }
.sb-stat-lbl { font-size: .75rem; color: var(--text-muted); }
.sb-nav { display: flex; flex-direction: column; gap: .25rem; }
.sb-nav-link {
  display: block;
  padding: .5rem .75rem;
  border-radius: var(--radius);
  font-size: .875rem;
  font-weight: 500;
  color: var(--text-muted);
  text-decoration: none;
  transition: background .15s, color .15s;
}
.sb-nav-link:hover, .sb-nav-link--active { background: var(--surface-3); color: var(--primary); }

/* Section headers */
.section-header { margin-bottom: 1.25rem; padding-bottom: .75rem; border-bottom: 1px solid var(--border-light); }
.section-title { font-size: 1.0625rem; font-weight: 700; color: var(--text); margin: 0 0 .25rem; }
.section-sub { font-size: .8125rem; color: var(--text-muted); margin: 0; }

/* Form fields */
.sc-field { display: flex; flex-direction: column; gap: .375rem; margin-bottom: 1rem; }
.sc-field label { font-size: .875rem; font-weight: 600; color: #334155; }
.sc-field input,
.sc-field select,
.sc-field textarea {
  width: 100%;
  padding: .625rem .875rem;
  border: 1.5px solid var(--border);
  border-radius: var(--radius);
  font-family: inherit;
  font-size: .9375rem;
  color: var(--text);
  background: #fff;
  outline: none;
  transition: border-color .15s, box-shadow .15s;
  box-sizing: border-box;
}
.sc-field input:focus,
.sc-field select:focus,
.sc-field textarea:focus { border-color: var(--primary); box-shadow: 0 0 0 3px rgba(30,58,95,.1); }
.sc-field input[readonly] { background: var(--surface-2); color: var(--text-muted); }
.req { color: var(--danger); }

/* Grid layouts */
.form-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 0 1.25rem; }
.form-grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 0 1.25rem; }
.form-sections { display: flex; flex-direction: column; }

/* Credentials block */
.credentials-block { margin-top: 1.25rem; padding-top: 1.25rem; border-top: 1px solid var(--border-light); }
.cred-title { font-size: .9375rem; font-weight: 700; color: var(--text); margin: 0 0 .875rem; }
.cred-name { font-weight: 400; color: var(--text-muted); margin-left: .5rem; font-size: .8125rem; }
.badge { display: inline-block; padding: .15rem .5rem; border-radius: var(--radius-full); font-size: .75rem; font-weight: 600; margin-left: .5rem; }
.badge--warn { background: #fef3c7; color: #92400e; }
.badge--ok   { background: #d1fae5; color: #065f46; }
.badge--err  { background: #fee2e2; color: #991b1b; }

/* Interests */
.interests-grid { display: flex; flex-wrap: wrap; gap: .5rem; }
.interest-chip {
  display: flex;
  align-items: center;
  gap: .375rem;
  padding: .375rem .75rem;
  border: 1.5px solid var(--border);
  border-radius: var(--radius-full);
  cursor: pointer;
  font-size: .8125rem;
  font-weight: 500;
  color: var(--text-muted);
  transition: border-color .15s, color .15s;
}
.interest-chip:has(input:checked) { border-color: var(--primary); color: var(--primary); background: rgba(30,58,95,.05); }
.interest-chip input { display: none; }

/* Alerts */
.sc-alert { padding: .75rem 1rem; border-radius: var(--radius); font-size: .875rem; margin-bottom: 1rem; }
.sc-alert--success { background: #f0fdf4; color: #15803d; border: 1px solid #bbf7d0; }
.sc-alert--error   { background: #fef2f2; color: #dc2626; border: 1px solid #fecaca; }

/* Save bar */
.save-bar { display: flex; justify-content: flex-end; gap: .75rem; padding: 1rem 0 2rem; }

/* Modal overlay */
.modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.5); z-index: 500; display: flex; align-items: center; justify-content: center; padding: 1rem; }
.modal-panel { background: #fff; border-radius: var(--radius-lg); box-shadow: var(--shadow-lg); width: 100%; max-width: 680px; max-height: 90vh; overflow-y: auto; }
.modal-panel--wide { max-width: 760px; }
.modal-header { display: flex; align-items: center; justify-content: space-between; padding: 1.25rem 1.5rem; border-bottom: 1px solid var(--border-light); }
.modal-title { font-size: 1.0625rem; font-weight: 700; color: var(--text); margin: 0; }
.modal-close { background: none; border: none; cursor: pointer; color: var(--text-muted); display: flex; align-items: center; padding: .25rem; }
.modal-body { padding: 1.5rem; }
.modal-footer { padding: 1rem 1.5rem; border-top: 1px solid var(--border-light); display: flex; justify-content: flex-end; gap: .75rem; }

/* Cropper */
.cropper-wrap { display: flex; gap: 1.5rem; align-items: flex-start; margin-top: 1rem; flex-wrap: wrap; }
.cropper-box { flex: 1; min-width: 280px; }
.cropper-preview { width: 120px; height: 120px; border-radius: 50%; object-fit: cover; border: 3px solid var(--border-light); flex-shrink: 0; }

/* Spinner */
.sc-spinner { display: inline-block; width: 16px; height: 16px; border: 2px solid rgba(255,255,255,.3); border-top-color: #fff; border-radius: 50%; animation: spin .7s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

@media (max-width: 768px) {
  .page-wrap { grid-template-columns: 1fr; }
  .sidebar-card { position: static; }
  .form-grid-2 { grid-template-columns: 1fr; }
  .form-grid-3 { grid-template-columns: 1fr 1fr; }
  .main-card { padding: 1.25rem; }
}
</style>
